import React, { useState } from "react";

import { AlertTriangle, Ban, FileX } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useToast } from "~/hooks/use-toast";
import {
  CreditNoteNullificationService,
  type NullifyCreditNoteRequest,
} from "~/services/creditNoteNullificationService";

interface NullifyCreditNoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  creditNote: any;
  onSuccess?: (result: any) => void;
}

export const NullifyCreditNoteDialog: React.FC<
  NullifyCreditNoteDialogProps
> = ({ isOpen, onClose, creditNote, onSuccess }) => {
  const [nullificationType, setNullificationType] = useState<
    "full" | "administrative"
  >("full");
  const [motive, setMotive] = useState("");
  const [administrativeReason, setAdministrativeReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const nullificationTypes =
    CreditNoteNullificationService.getNullificationTypes();

  const handleReset = () => {
    setNullificationType("full");
    setMotive("");
    setAdministrativeReason("");
    setIsSubmitting(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const isFormValid = (): boolean => {
    if (!motive.trim() || motive.trim().length < 10) {
      return false;
    }

    if (
      nullificationType === "administrative" &&
      (!administrativeReason.trim() || administrativeReason.trim().length < 10)
    ) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa todos los campos requeridos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const request: NullifyCreditNoteRequest = {
        sales_request_document_id: creditNote.id,
        motive: motive.trim(),
        nullification_type: nullificationType,
        administrative_reason:
          nullificationType === "administrative"
            ? administrativeReason.trim()
            : undefined,
      };

      // Validate the request
      const validationErrors =
        CreditNoteNullificationService.validateNullificationRequest(request);

      if (validationErrors.length > 0) {
        toast({
          title: "Errores de validación",
          description: validationErrors.join(", "),
          variant: "destructive",
        });
        return;
      }

      const result =
        await CreditNoteNullificationService.nullifyCreditNote(request);

      toast({
        title: "Nota de crédito anulada",
        description: result.message,
        variant: "default",
      });

      if (onSuccess) {
        onSuccess(result);
      }

      handleClose();
    } catch (error) {
      console.error("Error nullifying credit note:", error);
      toast({
        title: "Error al anular nota de crédito",
        description:
          error instanceof Error
            ? error.message
            : "Ocurrió un error inesperado",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!creditNote) {
    return null;
  }

  // Check if the credit note can be nullified
  const { canNullify, reason } =
    CreditNoteNullificationService.canNullify(creditNote);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileX className="h-5 w-5 text-red-600" />
            Anular Nota de Crédito
          </DialogTitle>
          <DialogDescription>
            Anula la nota de crédito {creditNote.bsale_number} por un valor de $
            {creditNote.total_amount?.toLocaleString() || "0"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Credit Note Information */}
          <div className="rounded-lg border bg-gray-50 p-4">
            <h4 className="mb-2 text-sm font-medium">
              Información de la Nota de Crédito
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Número:</span>{" "}
                {creditNote.bsale_number || "N/A"}
              </div>
              <div>
                <span className="text-gray-600">Monto:</span> $
                {creditNote.total_amount?.toLocaleString() || "0"}
              </div>
              <div>
                <span className="text-gray-600">Estado:</span>{" "}
                <Badge
                  variant={creditNote.cancelled ? "destructive" : "default"}
                >
                  {creditNote.cancelled ? "Anulada" : "Activa"}
                </Badge>
              </div>
              <div>
                <span className="text-gray-600">Tipo:</span>{" "}
                {creditNote.credit_note_type || "N/A"}
              </div>
            </div>
          </div>

          {/* Validation Check */}
          {!canNullify && (
            <Alert variant="destructive">
              <Ban className="h-4 w-4" />
              <AlertDescription>
                No se puede anular esta nota de crédito: {reason}
              </AlertDescription>
            </Alert>
          )}

          {canNullify && (
            <>
              {/* Nullification Type Selection */}
              <div className="space-y-2">
                <Label htmlFor="nullification-type">Tipo de Anulación</Label>
                <Select
                  value={nullificationType}
                  onValueChange={(value) =>
                    setNullificationType(value as "full" | "administrative")
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo de anulación" />
                  </SelectTrigger>
                  <SelectContent>
                    {nullificationTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{type.label}</span>
                          <span className="text-xs text-gray-500">
                            {type.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Motive */}
              <div className="space-y-2">
                <Label htmlFor="motive">
                  Motivo de Anulación <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="motive"
                  value={motive}
                  onChange={(e) => setMotive(e.target.value)}
                  placeholder="Ingresa el motivo detallado para anular la nota de crédito (mínimo 10 caracteres)"
                  rows={3}
                  className="resize-none"
                  maxLength={500}
                />
                <div className="text-right text-xs text-gray-500">
                  {motive.length}/500 caracteres (mínimo 10)
                </div>
              </div>

              {/* Administrative Reason (only for administrative nullification) */}
              {nullificationType === "administrative" && (
                <div className="space-y-2">
                  <Label htmlFor="admin-reason">
                    Razón Administrativa <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="admin-reason"
                    value={administrativeReason}
                    onChange={(e) => setAdministrativeReason(e.target.value)}
                    placeholder="Especifica la razón administrativa para esta anulación"
                    rows={2}
                    className="resize-none"
                    maxLength={300}
                  />
                  <div className="text-right text-xs text-gray-500">
                    {administrativeReason.length}/300 caracteres (mínimo 10)
                  </div>
                </div>
              )}

              {/* Warning Alert */}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {nullificationType === "full"
                    ? "La anulación completa cancelará completamente la nota de crédito y se declarará al SII. Esta acción no se puede deshacer."
                    : "La anulación administrativa permite cancelar la nota de crédito por motivos específicos. Esta acción no se puede deshacer."}
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        <DialogFooter className="flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          {canNullify && (
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className={`w-full sm:w-auto ${
                nullificationType === "full"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-purple-600 hover:bg-purple-700"
              }`}
            >
              {isSubmitting
                ? "Procesando..."
                : nullificationType === "full"
                  ? "Anular Completamente"
                  : "Anular Administrativamente"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
